import { CampaignScheduling, UniversalTimeConfig } from '@/routes/campaigns/types';
import { TimeZone } from '@vvo/tzdb';

const TIMEZONE_DISPLAY: Record<string, string> = {
  'America/Los_Angeles': 'PT',
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Phoenix': 'MST',
  'Pacific/Honolulu': 'HST',
  'America/Anchorage': 'AKT'
} as const;

export const getTimeZoneDisplay = (scheduled?: CampaignScheduling) => {
  if (!scheduled?.config) {
    // If no scheduling config, use browser's timezone
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_DISPLAY[userTimeZone] || 'Local';
  }

  if (scheduled.config.type === 'local') {
    return 'Local';
  }

  // Universal time - get from timezone in config
  const universalConfig = scheduled.config.config as UniversalTimeConfig;
  return TIMEZONE_DISPLAY[universalConfig.timezone] || universalConfig.timezone;
};

interface FormatNumberOptions {
  style?: 'currency' | 'decimal' | 'percent';
  currency?: string;
  notation?: 'standard' | 'compact';
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  suffix?: string;
}

export function formatNumber(value: number, options: FormatNumberOptions = {}): string {
  const {
    style = 'decimal',
    currency = 'USD',
    notation = 'standard',
    maximumFractionDigits = 2,
    minimumFractionDigits = maximumFractionDigits,
    suffix = ''
  } = options;

  const formatted = new Intl.NumberFormat('en-US', {
    style,
    currency,
    notation,
    maximumFractionDigits,
    minimumFractionDigits
  }).format(value);

  return `${formatted}${suffix}`;
}

export function formatCurrency(value: number, decimals = 2): string {
  return formatNumber(value, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals
  });
}

export function formatRoI(value: number, decimals = 2): string {
  return formatNumber(value, {
    suffix: 'x',
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals
  });
}

export function formatMetric(value: number, decimals = 2): string {
  return formatNumber(value, { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
}

export function formatTimezone(timezone: TimeZone): string {
  return `(${timezone.abbreviation}) ${timezone.alternativeName} - GMT${timezone.rawFormat.split(' ')[0]}`;
}
